import { Editor } from '@monaco-editor/react';
import { useState } from 'react';

import { renderHtmlWithTeX } from './../../../utils/RenderQuestionWithTex';

type CmsProgrammingPropsType = {
  question: any;
};

const CmsProgramming = ({ question }: CmsProgrammingPropsType) => {
  const [selectedStarterCode, setSelectedStarterCode] = useState('');
  const [selectedIdealSolution, setSelectedIdealSolution] = useState('');

  const idealSolution = question?.idealSolutions?.find(
    (each: any) => each.language === selectedIdealSolution
  );

  const starterCode = question?.starterCode?.find(
    (each: any) => each.language === selectedStarterCode
  )?.code;

  return (
    <div className='mx-auto max-w-7xl space-y-8 p-6'>
      <p>Title: {question?.title}</p>
      <div className='gap-3 md:flex md:w-full'>
        <div className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:w-[50%]'>
          <div className='border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4'>
            <h2 className='text-lg font-semibold text-gray-800'>
              Ideal Solution
            </h2>
          </div>
          <div className='p-6'>
            <div className='mb-4 flex flex-wrap gap-2'>
              {question?.idealSolutions?.map((each: any) => (
                <button
                  key={each?.language}
                  onClick={() => setSelectedIdealSolution(each?.language)}
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                    selectedIdealSolution === each?.language
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {each?.language}
                </button>
              ))}
            </div>
            {idealSolution && (
              <div className='overflow-hidden rounded-md border border-gray-200'>
                <Editor
                  height='200px'
                  language={selectedIdealSolution.toLowerCase()}
                  value={idealSolution?.answerCode}
                  theme='vs-dark'
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 14
                  }}
                />
                <p className='p-3'>
                  Explanation:
                  {renderHtmlWithTeX(idealSolution?.explanation, false)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Starter Code Section */}
        <div className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:w-[50%]'>
          <div className='border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4'>
            <h2 className='text-lg font-semibold text-gray-800'>
              Starter Code
            </h2>
          </div>
          <div className='p-6'>
            <div className='mb-4 flex flex-wrap gap-2'>
              {question?.starterCode?.map((each: any) => (
                <button
                  key={each?.language}
                  onClick={() => setSelectedStarterCode(each?.language)}
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                    selectedStarterCode === each?.language
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {each?.language}
                </button>
              ))}
            </div>
            {starterCode && (
              <div className='overflow-hidden rounded-md border border-gray-200'>
                <Editor
                  height='150px'
                  language={selectedStarterCode.toLowerCase()}
                  value={starterCode}
                  theme='vs-dark'
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 14
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Test Cases Section */}
      <div className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'>
        <div className='border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4'>
          <h2 className='text-lg font-semibold text-gray-800'>Test Cases</h2>
        </div>
        <div className='p-6'>
          <div className='flex flex-wrap gap-3 space-y-4'>
            {question?.testCases?.map((each: any, index: number) => (
              <div
                key={each?.input}
                className='rounded-lg border border-gray-200 bg-gray-50 p-4'
              >
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <div>
                    <span className='text-sm font-semibold uppercase tracking-wide text-gray-600'>
                      Input:
                    </span>
                    <pre className='mt-2 overflow-x-auto rounded border border-gray-200 bg-white p-3 text-sm text-gray-800'>
                      {each?.input}
                    </pre>
                  </div>
                  <div>
                    <span className='text-sm font-semibold uppercase tracking-wide text-gray-600'>
                      Output:
                    </span>
                    <pre className='mt-2 overflow-x-auto rounded border border-gray-200 bg-white p-3 text-sm text-gray-800'>
                      {each?.output}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsProgramming;
